import * as React from 'react'

import { ChatMessage } from './ChatMessage'
import { renderBoldSegments } from './renderBoldSegments'
import { CompetitorConfirmTurn } from './turns/CompetitorConfirmTurn'
import { ContextTurn } from './turns/ContextTurn'
import { DemoResponseTurn } from './turns/DemoResponseTurn'
import { PlatformPickerTurn } from './turns/PlatformPickerTurn'
import { PromptsTurn } from './turns/PromptsTurn'
import { ScanningTurn } from './turns/ScanningTurn'
import { WebsiteTurn } from './turns/WebsiteTurn'
import type { OnboardingAnswers, PromptsErrorKind, Turn } from './useOnboardingMachine'

import { formatPlatformName } from '@/components/dashboard/PlatformIcon'
import { excludeOwnBrand, filterMentionPills } from '@/lib/mentions'

interface TranscriptTurnProps {
  turn: Turn
  answers: OnboardingAnswers
  websiteError: string | null
  websiteSaving: boolean
  canCreateClients: boolean
  contextSaving: boolean
  contextError: string | null
  promptsLoading: boolean
  promptsErrorKind: PromptsErrorKind | null
  promptsSaving: boolean
  onSubmitWebsite: (url: string, clientName?: string) => void
  onSubmitContext: (industry: string, niche: string, accepted: boolean) => void
  onSubmitPrompts: (selected: string) => void
  onSkipPrompts: () => void
  onRetryPrompts: () => void
  onSelectPlatform: (platform: string) => void
  platformSaving: boolean
  onContinueToCompetitors: () => void
  onRetryDemo: () => void
  onConfirmCompetitors: (names: string[]) => void
  competitorsSaving: boolean
  focusRef?: React.Ref<HTMLInputElement> | undefined
  contextNiche: string
  onContextNicheChange: (value: string) => void
  promptsSelected: string | null
  onTogglePrompt: (prompt: string) => void
  onAddCustomPrompt: (prompt: string) => void
  competitorExtras: string[]
}

export function TranscriptTurn(props: TranscriptTurnProps) {
  const { turn, answers } = props

  if (turn.kind === 'text' || turn.kind === 'status') {
    return (
      <ChatMessage role={turn.role}>
        {turn.kind === 'status' ? (
          <ScanningTurn text={turn.text ?? ''} />
        ) : (
          renderBoldSegments(turn.text ?? '')
        )}
      </ChatMessage>
    )
  }

  const competitorNames = answers.demoResult
    ? excludeOwnBrand(filterMentionPills(answers.demoResult.competitor_mentions), {
        host: answers.host,
        brandName: answers.demoResult.brand_name,
      })
    : []

  return (
    <ChatMessage role="assistant">
      <div className="space-y-3">
        <p>{renderBoldSegments(turn.text ?? '')}</p>
        {turn.component === 'website' && (
          <WebsiteTurn
            resolved={!!turn.resolved}
            prefill={answers.websiteUrl ?? ''}
            submittedUrl={answers.host}
            saving={props.websiteSaving}
            error={props.websiteError}
            onSubmit={props.onSubmitWebsite}
            autoFocusRef={props.focusRef}
            canCreateClients={props.canCreateClients}
            clientNamePrefill={answers.clientName ?? ''}
          />
        )}
        {(turn.component === 'context' || turn.component === 'context_manual') && (
          <ContextTurn
            resolved={!!turn.resolved}
            summary={answers.industry ? `${answers.industry}${answers.nicheDescription ? ` — ${answers.nicheDescription}` : ''}` : undefined}
            prefillIndustry={turn.component === 'context' ? answers.industry : undefined}
            prefillNiche={turn.component === 'context' ? answers.nicheDescription : undefined}
            saving={props.contextSaving}
            error={props.contextError}
            onSubmit={props.onSubmitContext}
            niche={props.contextNiche}
            onNicheChange={props.onContextNicheChange}
          />
        )}
        {turn.component === 'prompts' && (
          <PromptsTurn
            resolved={!!turn.resolved}
            summary={answers.demoPrompt ? `Test: "${answers.demoPrompt}"` : 'Skipped for now'}
            prompts={answers.suggestedPrompts ?? []}
            usedAi={!!answers.usedAi}
            loading={props.promptsLoading}
            errorKind={props.promptsErrorKind}
            saving={props.promptsSaving}
            onRetry={props.onRetryPrompts}
            onSubmit={props.onSubmitPrompts}
            onSkip={props.onSkipPrompts}
            selected={props.promptsSelected}
            onToggle={props.onTogglePrompt}
            onAddCustom={props.onAddCustomPrompt}
          />
        )}
        {turn.component === 'platform_picker' && (
          <PlatformPickerTurn
            resolved={!!turn.resolved}
            summary={answers.demoPlatform ? formatPlatformName(answers.demoPlatform) : undefined}
            saving={props.platformSaving}
            onSelect={props.onSelectPlatform}
          />
        )}
        {turn.component === 'demo_response' && (
          <DemoResponseTurn
            result={answers.demoResult}
            onContinue={props.onContinueToCompetitors}
            onRetry={props.onRetryDemo}
          />
        )}
        {turn.component === 'competitor_confirm' && (
          <CompetitorConfirmTurn
            resolved={!!turn.resolved}
            summary={
              answers.confirmedCompetitors
                ? answers.confirmedCompetitors.length === 0
                  ? 'None of these are competitors.'
                  : `Confirmed ${answers.confirmedCompetitors.length} competitor${answers.confirmedCompetitors.length === 1 ? '' : 's'}.`
                : undefined
            }
            names={competitorNames}
            saving={props.competitorsSaving}
            onConfirm={props.onConfirmCompetitors}
            onSkip={() => props.onConfirmCompetitors([])}
            composerExtras={props.competitorExtras}
          />
        )}
      </div>
    </ChatMessage>
  )
}
